import { useEffect, useMemo, useState } from "react";
import { Sparkles, X, Loader2, Building2, MapPin, Send, Mail, ShieldCheck, Trophy, CheckSquare, Square } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const isPriorityRole = (position) => {
  const p = (position || "").toLowerCase();
  return p.includes("recruit") || p.includes("head coach");
};

/**
 * Find similar coaches & one-click share with them.
 * Opens from the Share Activity row 3-dot menu when there's an anchor share to seed from.
 */
export function SimilarCoachesModal({ open, seedShare, athleteId, athleteName, onClose, onShared }) {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState({}); // coach_id -> bool
  const [sent, setSent] = useState({}); // coach_id -> bool
  const [selected, setSelected] = useState({}); // coach_id -> bool
  const [bulkSending, setBulkSending] = useState(false);

  useEffect(() => {
    if (!open || !seedShare?.share_id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setCoaches([]); setSent({}); setSelected({});
      try {
        const token = localStorage.getItem("token");
        const r = await fetch(`${API}/api/players/${athleteId}/shares/${seedShare.share_id}/similar-coaches?limit=15`, {
          headers: { "Authorization": `Bearer ${token}` },
        });
        const txt = await r.text();
        let data = {};
        try { data = JSON.parse(txt); } catch {}
        if (!cancelled) {
          if (!r.ok) {
            toast.error(data.detail || "Failed to load similar coaches");
          } else {
            const list = data.candidates || [];
            setCoaches(list);
            // Smart default: select recruiting coordinators + head coaches
            const defaults = {};
            list.forEach((c) => { if (isPriorityRole(c.position)) defaults[c.coach_id] = true; });
            setSelected(defaults);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, seedShare?.share_id, athleteId]);

  const selectedCount = useMemo(
    () => coaches.filter((c) => selected[c.coach_id] && !sent[c.coach_id]).length,
    [coaches, selected, sent]
  );
  const visibleSelectableCount = coaches.filter((c) => !sent[c.coach_id]).length;
  const allSelected = visibleSelectableCount > 0 && selectedCount === visibleSelectableCount;

  if (!open) return null;

  const close = () => { setCoaches([]); setSent({}); setSelected({}); onClose && onClose(); };

  const toggleAll = () => {
    if (allSelected) {
      setSelected({});
    } else {
      const next = {};
      coaches.forEach((c) => { if (!sent[c.coach_id]) next[c.coach_id] = true; });
      setSelected(next);
    }
  };

  const toggleOne = (coachId) => setSelected((s) => ({ ...s, [coachId]: !s[coachId] }));

  const handleShareOne = async (coach) => {
    if (!coach?.email) return;
    setSending((s) => ({ ...s, [coach.coach_id]: true }));
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API}/api/players/${athleteId}/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({
          coach_email: coach.email,
          coach_name: coach.name || `${coach.first_name || ""} ${coach.last_name || ""}`.trim(),
          coach_school: coach.school || "",
          note: "",
        }),
      });
      const txt = await r.text();
      let data = {};
      try { data = JSON.parse(txt); } catch {}
      if (!r.ok) throw new Error(data.detail || "Share failed");
      setSent((s) => ({ ...s, [coach.coach_id]: true }));
      toast.success(`Shared with ${coach.name}`);
      onShared && onShared();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSending((s) => ({ ...s, [coach.coach_id]: false }));
    }
  };

  const handleBulkShare = async () => {
    const targets = coaches.filter((c) => selected[c.coach_id] && !sent[c.coach_id] && c.email);
    if (targets.length === 0) {
      toast.error("Select at least one coach first");
      return;
    }
    setBulkSending(true);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API}/api/players/${athleteId}/shares/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({
          coaches: targets.map((c) => ({
            coach_email: c.email,
            coach_name: c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim(),
            coach_school: c.school || "",
          })),
          note: "",
        }),
      });
      const txt = await r.text();
      let data = {};
      try { data = JSON.parse(txt); } catch {}
      if (!r.ok) throw new Error(data.detail || "Bulk share failed");
      // Mark all newly-created ones as "sent"
      const emailToCoachId = {};
      targets.forEach((c) => { emailToCoachId[(c.email || "").toLowerCase()] = c.coach_id; });
      const nextSent = { ...sent };
      (data.results || []).forEach((r) => {
        if (r.status === "created" || r.status === "already_shared") {
          const id = emailToCoachId[r.coach_email];
          if (id) nextSent[id] = true;
        }
      });
      setSent(nextSent);
      const skippedMsg = data.skipped ? ` · ${data.skipped} already shared` : "";
      toast.success(`Shared with ${data.created} coach${data.created === 1 ? "" : "es"}${skippedMsg}`);
      onShared && onShared();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBulkSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="similar-coaches-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="bg-gradient-to-r from-purple-900 via-blue-900 to-blue-700 px-5 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center">
              <Sparkles size={16} className="text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-base leading-tight">Find More Coaches Like This</h2>
              <p className="text-blue-200 text-[11px]">
                Based on {seedShare?.coach_school || "the program"} you already reached out to
              </p>
            </div>
          </div>
          <button onClick={close} className="text-white/70 hover:text-white" data-testid="similar-coaches-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="animate-spin mx-auto text-blue-700 mb-3" size={24} />
              <p className="text-sm text-gray-500">Finding programs similar to <strong>{seedShare?.coach_school}</strong>…</p>
            </div>
          ) : coaches.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-500">
              No similar coaches found that you haven't already contacted.
            </div>
          ) : (
            <>
              <div className="rounded-lg bg-purple-50 border border-purple-200 p-3 mb-4 text-xs text-purple-900 flex gap-2">
                <Sparkles size={14} className="flex-shrink-0 mt-0.5 text-purple-600" />
                <p>
                  We surfaced <strong>{coaches.length} highly-targeted coaches</strong> at programs in the same conference, division, or region.
                  We pre-selected <strong>recruiting coordinators and head coaches</strong> — the real decision-makers. Add or remove any below.
                </p>
              </div>

              {/* Select All header */}
              <button
                onClick={toggleAll}
                className="w-full flex items-center gap-2 mb-2 text-xs font-bold text-gray-600 hover:text-gray-900 px-2 py-1.5 rounded hover:bg-gray-50"
                data-testid="similar-select-all"
              >
                {allSelected ? <CheckSquare size={14} className="text-blue-700" /> : <Square size={14} />}
                {allSelected ? "Deselect all" : "Select all"}
              </button>

              <div className="space-y-2">
                {coaches.map((c) => {
                  const isSending = !!sending[c.coach_id];
                  const isSent = !!sent[c.coach_id];
                  const isSelected = !!selected[c.coach_id];
                  const priority = isPriorityRole(c.position);
                  return (
                    <div
                      key={c.coach_id}
                      className={`rounded-xl border p-3.5 flex items-start gap-3 transition ${
                        isSent ? "bg-green-50/40 border-green-200"
                          : isSelected ? "bg-blue-50/40 border-blue-200"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      data-testid={`similar-coach-${c.coach_id}`}
                    >
                      <button
                        onClick={() => !isSent && toggleOne(c.coach_id)}
                        disabled={isSent}
                        className={`mt-1 flex-shrink-0 ${isSent ? "opacity-40" : "hover:scale-110 transition"}`}
                        data-testid={`similar-coach-check-${c.coach_id}`}
                      >
                        {isSelected || isSent ? <CheckSquare size={16} className="text-blue-700" /> : <Square size={16} className="text-gray-400" />}
                      </button>

                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center text-blue-700 flex-shrink-0">
                        <Building2 size={14} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-bold text-gray-900 truncate">{c.name}</span>
                          {priority && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase">
                              <Trophy size={9} /> Decision Maker
                            </span>
                          )}
                          {c.match_reason && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-[10px] font-bold uppercase">
                              <Sparkles size={9} /> {c.match_reason}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-600 mt-0.5 truncate">{c.position}</div>
                        <div className="text-xs text-gray-500 mt-1 flex flex-wrap items-center gap-2">
                          <span className="font-semibold">{c.school}</span>
                          {c.division && <span className="inline-flex items-center gap-1 text-gray-400">{c.division}</span>}
                          {c.state && <span className="inline-flex items-center gap-1 text-gray-400"><MapPin size={10} /> {c.state}</span>}
                        </div>
                        <div className="text-[11px] text-gray-400 mt-1 flex items-center gap-1 truncate">
                          <Mail size={10} /> {c.email}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        {isSent ? (
                          <div className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-green-50 text-green-700 text-xs font-bold">
                            <ShieldCheck size={12} /> Sent
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => handleShareOne(c)}
                            disabled={isSending || bulkSending}
                            variant="outline"
                            className="text-xs"
                            data-testid={`similar-coach-send-${c.coach_id}`}
                          >
                            {isSending ? <Loader2 size={12} className="animate-spin" /> : <><Send size={12} className="mr-1.5" /> Share</>}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="border-t border-gray-200 px-5 py-3 flex items-center justify-between gap-2 flex-shrink-0 bg-gray-50">
          <div className="text-xs text-gray-500">
            {selectedCount > 0 ? (
              <><strong className="text-gray-900">{selectedCount}</strong> coach{selectedCount === 1 ? "" : "es"} selected</>
            ) : (
              "Select coaches above"
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={close} data-testid="similar-coaches-done-btn">Done</Button>
            <Button
              onClick={handleBulkShare}
              disabled={selectedCount === 0 || bulkSending}
              className="bg-gradient-to-r from-purple-700 to-blue-700 hover:from-purple-800 hover:to-blue-800 text-white font-bold shadow"
              data-testid="similar-coaches-bulk-share-btn"
            >
              {bulkSending ? (<><Loader2 size={12} className="animate-spin mr-1.5" /> Sharing…</>) : (<><Send size={12} className="mr-1.5" /> Share with {selectedCount || 0}</>)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

